import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { NativeSelect } from "@/components/NativeSelect";
import { useAuth } from "@/context/AuthContext";
import { api, errText } from "@/lib/api";
import { PLAN_NAMES, formatDate, formatMoney } from "@/lib/constants";

const card = "rounded-2xl bg-white border border-[#d1e7dd] p-5 space-y-3";

export default function AdminBilling() {
  const { refreshSettings } = useAuth();
  const [s, setS] = useState(null);
  const [token, setToken] = useState("");
  const [receipts, setReceipts] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/admin/billing/receipts").then((r) => setReceipts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch, msg = "Сохранено") => {
    try { const { data } = await api.put("/admin/settings", patch); setS(data); refreshSettings(); toast.success(msg); }
    catch (e) { toast.error(errText(e)); }
  };
  if (!s) return <p className="text-sm text-[#6b8a7b]">Загрузка...</p>;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });

  return (
    <div className="space-y-5">
      <div className="grid lg:grid-cols-2 gap-5">
        <div className={card}>
          <h3 className="font-display font-bold text-lg">Цены и доступ</h3>
          <div className="grid grid-cols-3 gap-2">
            <label className="text-sm">Basic<Input type="number" min={1} value={s.price_basic} onChange={set("price_basic")} data-testid="billing-price-basic-input" /></label>
            <label className="text-sm">Pro<Input type="number" min={1} value={s.price_pro} onChange={set("price_pro")} data-testid="billing-price-pro-input" /></label>
            <label className="text-sm">Валюта
              <NativeSelect value={s.currency} onChange={set("currency")} data-testid="billing-currency-select">
                {["KZT", "RUB", "USD", "EUR"].map((c) => <option key={c}>{c}</option>)}
              </NativeSelect>
            </label>
          </div>
          <label className="text-sm block">Ссылка на страницу доната
            <Input placeholder="https://www.donationalerts.com/r/your_name" value={s.donation_url} onChange={set("donation_url")} data-testid="billing-donation-url-input" />
          </label>
          <Button className="rounded-full bg-[#1f5f42] hover:bg-[#174731]" data-testid="billing-save-button"
            onClick={() => save({ price_basic: Number(s.price_basic), price_pro: Number(s.price_pro), currency: s.currency, donation_url: s.donation_url.trim() })}>Сохранить</Button>
          <div className="flex items-center justify-between gap-4 pt-3 border-t border-[#eef6f1]">
            <div>
              <div className="font-semibold text-[#0b2e20]">Запретить скачивание бесплатным</div>
              <div className="text-xs text-[#4b6b5c]">Без Basic/Pro файлы можно только смотреть на сайте</div>
            </div>
            <Switch checked={s.downloads_restricted} onCheckedChange={(v) => save({ downloads_restricted: v }, v ? "Скачивание только для Basic и Pro" : "Скачивание открыто для всех")} data-testid="billing-restrict-downloads-toggle" />
          </div>
        </div>
        <div className={card}>
          <h3 className="font-display font-bold text-lg">DonationAlerts</h3>
          <p className="text-sm" data-testid="billing-mode">
            Режим: {s.payments_demo ? <b className="text-amber-700">демо (токен не задан)</b> : <b className="text-[#1f5f42]">реальные платежи</b>}
          </p>
          <p className="text-xs text-[#4b6b5c]">Вставьте access token DonationAlerts с правом oauth-donation-index. Сайт будет сам искать донат с кодом заказа в сообщении и выдавать чек. Токен хранится зашифрованным и больше не показывается.</p>
          <Input type="password" placeholder="Access token" value={token} onChange={(e) => setToken(e.target.value)} data-testid="billing-token-input" />
          <div className="flex gap-2 flex-wrap">
            <Button disabled={!token.trim()} className="rounded-full bg-[#1f5f42] hover:bg-[#174731]" onClick={() => { save({ da_token: token }, "Токен сохранён"); setToken(""); }} data-testid="billing-token-save-button">Сохранить токен</Button>
            {!s.payments_demo && <Button variant="outline" className="rounded-full" onClick={() => save({ da_token: "" }, "Токен удалён, включён демо-режим")} data-testid="billing-token-remove-button">Удалить токен</Button>}
          </div>
        </div>
      </div>
      <div className="rounded-2xl bg-white border border-[#d1e7dd] divide-y divide-[#eef6f1]" data-testid="admin-receipts-list">
        <div className="p-4 font-display font-bold">Чеки</div>
        {receipts.length === 0 && <p className="p-6 text-center text-sm text-[#6b8a7b]">Покупок пока нет</p>}
        {receipts.map((r) => (
          <div key={r.id} className="p-4 text-sm flex flex-wrap gap-x-3 gap-y-1" data-testid={`admin-receipt-${r.id}`}>
            <span className="font-semibold text-[#1f5f42]">{r.username}</span>
            <span>{PLAN_NAMES[r.plan]} · {formatMoney(r.amount, r.currency)}</span>
            <span className="text-[#6b8a7b]">№ {r.number} · {formatDate(r.paid_at)} · до {formatDate(r.valid_until)}</span>
            {r.demo && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-amber-100 text-amber-700">демо</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
